import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  Search, Star, SlidersHorizontal, X, ChevronLeft, ChevronRight,
  Briefcase, Filter
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Web Development', 'Mobile Development', 'UI/UX Design', 'Graphic Design',
  'Content Writing', 'SEO & Marketing', 'Data Science', 'Video & Animation',
  'Music & Audio', 'Business Consulting', 'Translation', 'Virtual Assistant'
];

const GigMarketplace = () => {
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(window.innerWidth > 768);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1, page_size: 12 });

  const [filters, setFilters] = useState({
    search: '',
    category: '',
    sortBy: 'created_at',
    priceRange: [0, 100000],
    minRating: 0
  });

  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    fetchGigs();
  }, [pagination.page]);

  const fetchGigs = async (resetPage = false) => {
    setLoading(true);
    try {
      const page = resetPage ? 1 : pagination.page;
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', '12');
      params.append('sort_by', filters.sortBy);

      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.priceRange[0] > 0) params.append('min_price', filters.priceRange[0]);
      if (filters.priceRange[1] < 100000) params.append('max_price', filters.priceRange[1]);
      if (filters.minRating > 0) params.append('min_rating', filters.minRating);

      const res = await axios.get(`${API}/gigs?${params.toString()}`);
      setGigs(res.data.items || []);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        total_pages: res.data.total_pages,
        page_size: res.data.page_size
      });

      // Build active filter tags
      const tags = [];
      if (filters.category) tags.push({ key: 'category', label: filters.category });
      if (filters.priceRange[0] > 0 || filters.priceRange[1] < 100000) {
        tags.push({ key: 'price', label: `₹${filters.priceRange[0].toLocaleString('en-IN')} - ₹${filters.priceRange[1].toLocaleString('en-IN')}` });
      }
      if (filters.minRating > 0) tags.push({ key: 'rating', label: `${filters.minRating}+ stars` });
      setActiveFilters(tags);
    } catch (error) {
      console.error('Failed to fetch gigs', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchGigs(true);
  };

  const clearFilter = (key) => {
    const newFilters = { ...filters };
    if (key === 'category') newFilters.category = '';
    if (key === 'price') newFilters.priceRange = [0, 100000];
    if (key === 'rating') newFilters.minRating = 0;
    setFilters(newFilters);
    setTimeout(() => fetchGigs(true), 0);
  };

  const clearAllFilters = () => {
    setFilters({ search: '', category: '', sortBy: 'created_at', priceRange: [0, 100000], minRating: 0 });
    setTimeout(() => fetchGigs(true), 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="marketplace-title">
            Gig Marketplace
          </h1>
          <p className="text-muted-foreground">Find the perfect freelancer for your project</p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-6" data-testid="search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search gigs, skills, or freelancers..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" data-testid="search-button">Search</Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </form>

        {/* Active Filter Tags */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
            {activeFilters.map(f => (
              <Badge key={f.key} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                {f.label}
                <button onClick={() => clearFilter(f.key)} className="ml-1 hover:bg-primary/10 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            <button onClick={clearAllFilters} className="text-xs text-muted-foreground hover:text-primary underline">
              Clear all
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-full md:w-64 flex-shrink-0 space-y-5" data-testid="filter-sidebar">
              {/* Category */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Category</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <Select
                    value={filters.category}
                    onValueChange={(v) => {
                      setFilters({ ...filters, category: v === 'all' ? '' : v });
                      setTimeout(() => fetchGigs(true), 0);
                    }}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Price Range */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Price Range</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0 space-y-3">
                  <Slider
                    value={filters.priceRange}
                    onValueChange={(v) => setFilters({ ...filters, priceRange: v })}
                    min={0}
                    max={100000}
                    step={500}
                    data-testid="price-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>₹{filters.priceRange[0].toLocaleString('en-IN')}</span>
                    <span>₹{filters.priceRange[1].toLocaleString('en-IN')}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => fetchGigs(true)} className="w-full text-xs">
                    Apply Price
                  </Button>
                </CardContent>
              </Card>

              {/* Minimum Rating */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Minimum Rating</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="flex gap-1">
                    {[0, 3, 3.5, 4, 4.5].map(r => (
                      <Button
                        key={r}
                        size="sm"
                        variant={filters.minRating === r ? 'default' : 'outline'}
                        className="text-xs px-2"
                        onClick={() => {
                          setFilters({ ...filters, minRating: r });
                          setTimeout(() => fetchGigs(true), 0);
                        }}
                        data-testid={`rating-filter-${r}`}
                      >
                        {r === 0 ? 'Any' : `${r}+`}
                        {r > 0 && <Star className="w-3 h-3 ml-0.5 fill-amber-400 text-amber-400" />}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Sort */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Sort By</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <Select
                    value={filters.sortBy}
                    onValueChange={(v) => {
                      setFilters({ ...filters, sortBy: v });
                      setTimeout(() => fetchGigs(true), 0);
                    }}
                  >
                    <SelectTrigger data-testid="sort-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Newest First</SelectItem>
                      <SelectItem value="price_low">Price: Low to High</SelectItem>
                      <SelectItem value="price_high">Price: High to Low</SelectItem>
                      <SelectItem value="rating">Highest Rated</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Gigs Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground" data-testid="results-count">
                {pagination.total} gig{pagination.total !== 1 ? 's' : ''} found
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-40 bg-slate-200 rounded-t-lg" />
                    <CardContent className="pt-4 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : gigs.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-heading font-semibold mb-2">No gigs found</h3>
                  <p className="text-muted-foreground mb-4">Try adjusting your filters or search terms</p>
                  <Button variant="outline" onClick={clearAllFilters}>Clear all filters</Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gigs.map(gig => (
                    <Card
                      key={gig.id}
                      className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
                      onClick={() => navigate(`/gigs/${gig.id}`)}
                      data-testid={`gig-card-${gig.id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {gig.freelancer_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="text-xs text-muted-foreground">{gig.freelancer_name}</span>
                        </div>
                        <CardTitle className="text-base line-clamp-2">{gig.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{gig.description}</p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {gig.tags?.slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                            <span className="text-sm font-medium">{gig.rating?.toFixed(1) || '0.0'}</span>
                            <span className="text-xs text-muted-foreground">({gig.reviews_count || 0})</span>
                          </div>
                          <span className="text-lg font-heading font-bold text-secondary">
                            ₹{gig.price?.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {Array.from({ length: Math.min(pagination.total_pages, 5) }, (_, i) => {
                      const start = Math.max(1, pagination.page - 2);
                      const pageNum = start + i;
                      if (pageNum > pagination.total_pages) return null;
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === pagination.page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPagination(p => ({ ...p, page: pageNum }))}
                          data-testid={`page-${pageNum}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      data-testid="next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GigMarketplace;
